"use client"

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import SvgIcon from '@components/shared/svg-icons/SvgIcon'
import mix from '@utils/styles/mix'
import { notify } from 'lib/notice/notice'
import { chatApi, errorOf } from './api'
import { dayKey, dayLabel, time } from './format'
import MessageText from './MessageText'
import MoreMenu from './MoreMenu'
import { BlockDialog, ReportDialog } from './Dialogs'
import Thumb from './Thumb'

import styles from './Messages.module.css'

const MAX = 2000
const POLL_MS = 5000
const UNDO_MS = 8000

export const SAFETY = 'Nie płać z góry nieznajomym i nie klikaj w linki do płatności przesłane w wiadomości.'

function Annotation ({ data }) {
    const icon = (name) => <SvgIcon name={name} size="sm" />
    if (data.salon) return <div className={styles.note}>{icon('building')}<span>{data.role === 'owner' ? 'Zapytanie wysłane z wizytówki Twojej firmy.' : 'Zapytanie wysłane z wizytówki firmy.'}</span></div>
    if (!data.offer) return <div className={styles.note}>{icon('info')}<span>Rozmowa bez ogłoszenia</span></div>
    const st = data.offer.state
    if (st === 'ended') return <div className={styles.note}>{icon('info')}<span>Ogłoszenie zakończone — historia rozmowy zostaje.</span></div>
    if (st === 'deleted') return <div className={styles.note}>{icon('info')}<span>Ogłoszenie usunięte — historia rozmowy zostaje.</span></div>
    if (st === 'hanged') return <div className={mix([styles.note, styles.noteWarn])}>{icon('alert')}<span>{data.role === 'owner' ? 'Ogłoszenie zawieszone — kupujący go nie widzą.' : 'Ogłoszenie jest teraz niedostępne.'}</span></div>
    return null
}

// Wysokość widocznej części ekranu (klawiatura na telefonie): pole wpisywania zostaje nad klawiaturą.
function useVisualViewport (active) {
    useEffect(() => {
        const vv = typeof window !== 'undefined' && window.visualViewport
        if (!active || !vv) return
        const root = document.documentElement.style
        const sync = () => {
            const full = Math.abs(vv.height - window.innerHeight) < 1 && vv.offsetTop === 0
            if (full) { root.removeProperty('--vvh'); root.removeProperty('--vv-top') }
            else { root.setProperty('--vvh', `${vv.height}px`); root.setProperty('--vv-top', `${vv.offsetTop}px`) }
        }
        sync()
        vv.addEventListener('resize', sync)
        vv.addEventListener('scroll', sync)
        return () => { vv.removeEventListener('resize', sync); vv.removeEventListener('scroll', sync); root.removeProperty('--vvh'); root.removeProperty('--vv-top') }
    }, [active])
}

export default function ConversationPane ({ id, token, mobile, backHref, onBack, onRead, onSent, onHidden, onCanonical }) {
    const [data, setData] = useState(null)
    const [error, setError] = useState('')
    const [text, setText] = useState('')
    const [problem, setProblem] = useState(null)
    const [sending, setSending] = useState(false)
    const [menu, setMenu] = useState(null)
    const [dialog, setDialog] = useState(null)
    const scrollRef = useRef(null)
    const headRef = useRef(null)
    const taRef = useRef(null)
    const stick = useRef(true)
    const lastId = useRef(null)

    useVisualViewport(mobile)

    const load = useCallback(async () => {
        setError('')
        const r = await chatApi(token, `/api/messages/conversations/${id}`)
        if (!r.body.success) { setError(errorOf(r, 'Nie udało się wczytać rozmowy.')); return }
        if (r.body.id !== id) onCanonical?.(r.body.id)
        lastId.current = r.body.messages.at(-1)?.id || null
        stick.current = true
        setData(r.body)
        onRead?.(r.body.id)
    }, [id, token])

    useEffect(() => { setData(null); setProblem(null); setText(''); load() }, [load])
    useEffect(() => { if (data) headRef.current?.focus({ preventScroll: true }) }, [Boolean(data)])

    // nowe wiadomości: co 5 s przy widocznej karcie, od razu po powrocie do karty
    useEffect(() => {
        if (!data) return
        let timer
        const poll = async () => {
            if (document.hidden) return
            const r = await chatApi(token, `/api/messages/conversations/${data.id}${lastId.current ? `?after=${lastId.current}` : ''}`)
            if (!r.body.success) return
            const fresh = r.body.messages || []
            if (fresh.length) {
                lastId.current = fresh.at(-1).id
                const el = scrollRef.current
                stick.current = !el || el.scrollHeight - el.scrollTop - el.clientHeight < 80
                setData(d => d && ({ ...d, other: r.body.other, blocking: r.body.blocking, offer: r.body.offer, messages: [...d.messages, ...fresh.filter(m => !d.messages.some(x => x.id === m.id))] }))
                onRead?.(data.id)
            } else {
                setData(d => d && ({ ...d, other: r.body.other, blocking: r.body.blocking, offer: r.body.offer }))
            }
        }
        const onVisible = () => { if (!document.hidden) poll() }
        timer = setInterval(poll, POLL_MS)
        document.addEventListener('visibilitychange', onVisible)
        return () => { clearInterval(timer); document.removeEventListener('visibilitychange', onVisible) }
    }, [data?.id, token])

    useLayoutEffect(() => {
        const el = scrollRef.current
        if (el && stick.current) el.scrollTop = el.scrollHeight
    }, [data?.messages?.length])

    const older = async () => {
        const first = data.messages[0]?.id
        const el = scrollRef.current
        const before = el ? el.scrollHeight - el.scrollTop : 0
        const r = await chatApi(token, `/api/messages/conversations/${data.id}?before=${first}`)
        if (!r.body.success) return notify(errorOf(r))
        stick.current = false
        setData(d => ({ ...d, more: r.body.more, messages: [...r.body.messages, ...d.messages] }))
        requestAnimationFrame(() => { if (el) el.scrollTop = el.scrollHeight - before })
    }

    const grow = (el) => { if (!el) return; el.style.height = 'auto'; el.style.height = `${Math.min(el.scrollHeight + 2, 140)}px` }
    useLayoutEffect(() => grow(taRef.current), [text])

    const send = async (retry) => {
        const value = (retry?.text ?? text).trim()
        if (!value || sending) return
        if (value.length > MAX) { setProblem({ title: 'Wiadomość jest za długa', text: `Skróć ją do ${MAX} znaków (teraz: ${value.length}).` }); return }
        setSending(true)
        const r = await chatApi(token, `/api/messages/conversations/${data.id}/send`, { method: 'POST', body: { text: value } })
        setSending(false)
        stick.current = true
        if (r.body.success) {
            setProblem(null)
            if (!retry) setText('')
            lastId.current = r.body.message.id
            setData(d => ({ ...d, messages: [...d.messages.filter(m => m.id !== retry?.id), r.body.message] }))
            onSent?.(data.id, r.body.message)
            taRef.current?.focus()
            return
        }
        if (r.status === 0) {
            const failed = { id: `fail-${Date.now()}`, text: value, at: new Date().toISOString(), mine: true, failed: true }
            if (!retry) setText('')
            setData(d => ({ ...d, messages: [...d.messages.filter(m => m.id !== retry?.id), failed] }))
            notify('Nie wysłano — brak połączenia. Wiadomość została w rozmowie, możesz ją ponowić.')
            return
        }
        const title = { link: 'Usuń link', word: 'Zmień słowo', blocked: 'Nie możesz wysłać wiadomości', deleted: 'Nie można odpowiedzieć', rate: 'Za szybko' }[r.body.code] || 'Nie wysłano'
        setProblem({ title, text: errorOf(r) })
        if (r.body.code === 'deleted') setData(d => ({ ...d, other: { ...d.other, deleted: true } }))
        taRef.current?.focus()
    }

    const onKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing && window.matchMedia('(min-width: 1024px)').matches) {
            e.preventDefault()
            send()
        }
    }

    const block = async (on) => {
        const r = await chatApi(token, `/api/messages/conversations/${data.id}/block`, { method: 'POST', body: { block: on } })
        if (!r.body.success) return notify(errorOf(r))
        setData(d => ({ ...d, blocking: on }))
        notify(on ? `Zablokowano: ${data.other?.name || 'użytkownik'}.` : `Odblokowano: ${data.other?.name || 'użytkownik'}.`, { ms: UNDO_MS, action: { label: 'Cofnij', onClick: () => block(!on) } })
    }

    const action = async (key) => {
        if (key === 'offer') { window.location.href = data.salon?.link || data.offer.link; return }
        if (key === 'report') return setDialog('report')
        if (key === 'block') return setDialog('block')
        if (key === 'unblock') return block(false)
        if (key === 'unread' || key === 'delete') {
            const r = await chatApi(token, '/api/messages/conversations/actions', { method: 'POST', body: { ids: [data.id], action: key } })
            if (!r.body.success) return notify(errorOf(r))
            if (key === 'unread') { notify('Oznaczono jako nieprzeczytaną.'); onHidden?.(data.id, 'unread'); return }
            onHidden?.(data.id, 'delete')
            notify('Usunięto rozmowę (tylko u Ciebie).', { ms: UNDO_MS, action: { label: 'Cofnij', onClick: async () => {
                await chatApi(token, '/api/messages/conversations/actions', { method: 'POST', body: { ids: [data.id], action: 'restore' } })
                onHidden?.(data.id, 'restore')
            } } })
        }
    }

    const report = async (form) => {
        const r = await chatApi(token, `/api/messages/conversations/${data.id}/report`, { method: 'POST', body: form })
        if (!r.body.success) { notify(errorOf(r)); return false }
        if (form.block) setData(d => ({ ...d, blocking: true }))
        notify(`Dziękujemy — zgłoszenie trafiło do zespołu Samochody.pl.${form.block ? ' Użytkownik zablokowany.' : ''}`)
        return true
    }

    if (error) {
        return (
            <div className={styles.paneEmpty}>
                <p>{error}</p>
                <button className={mix([styles.btn, styles.secondary])} onClick={load} type="button">Spróbuj ponownie</button>
                { mobile && <a className={mix([styles.btn, styles.ghost])} href={backHref} onClick={onBack}>Wróć do listy</a> }
            </div>
        )
    }
    if (!data) return <div aria-busy="true" className={styles.paneEmpty}><span className={styles.sr}>Wczytywanie rozmowy</span></div>

    const other = data.other || { name: 'Użytkownik', firm: false, deleted: false }
    const link = (data.offer && data.offer.state !== 'deleted' && data.offer.link) || data.salon?.link
    const items = [
        link && { key: 'offer', label: data.salon ? 'Przejdź do wizytówki' : 'Przejdź do ogłoszenia', icon: 'eye' },
        { key: 'unread', label: 'Oznacz jako nieprzeczytaną', icon: 'chat' },
        !other.deleted && { key: 'report', label: 'Zgłoś rozmowę', icon: 'flag' },
        !other.deleted && (data.blocking ? { key: 'unblock', label: 'Odblokuj użytkownika', icon: 'block' } : { key: 'block', label: 'Zablokuj użytkownika', icon: 'block' }),
        { key: 'delete', label: 'Usuń rozmowę', icon: 'trash', hint: 'Rozmowa zniknie tylko u Ciebie', danger: true },
    ].filter(Boolean)

    let prevDay = ''
    const count = text.length

    return (
        <>
            <div className={styles.head}>
                <a aria-label="Wróć do listy" className={mix([styles.iconBtn, styles.back])} href={backHref} onClick={onBack}><SvgIcon name="chev-l" /></a>
                <Thumb offer={data.offer} salon={data.salon} />
                <div className={styles.info}>
                    <h2 id="conv-h" ref={headRef} tabIndex={-1}>
                        { link ? <a href={link} title={data.salon ? 'Otwórz wizytówkę' : 'Otwórz ogłoszenie'}>{data.title}</a> : <span>{data.title}</span> }
                    </h2>
                    <span className={styles.sub}>{ data.offer?.price && <><b>{data.offer.price}</b> · </> }{other.name}{other.firm ? ' · firma' : ''}</span>
                </div>
                <button aria-expanded={Boolean(menu)} aria-haspopup="menu" aria-label="Więcej działań" className={styles.iconBtn} onClick={(e) => setMenu(e.currentTarget)} type="button"><SvgIcon name="more" /></button>
            </div>
            <Annotation data={data} />
            <div className={styles.scroll} ref={scrollRef}>
                <p className={styles.safety}><SvgIcon name="shield" size="sm" /><span>{SAFETY}</span></p>
                { data.more && <div className={styles.older}><button className={mix([styles.btn, styles.ghost])} onClick={older} type="button">Starsze wiadomości</button></div> }
                <ol aria-label="Wiadomości" aria-live="polite" className={styles.bubbles} role="log">
                    { data.messages.map(m => {
                        const d = new Date(m.at), k = dayKey(d)
                        const sep = k !== prevDay
                        prevDay = k
                        return [
                            sep && <li className={styles.day} key={`d${k}`}><span>{dayLabel(d)}</span></li>,
                            <li className={mix([styles.b, m.mine && styles.bMe, m.failed && styles.bFail])} key={m.id}>
                                <span className={styles.sr}>{m.mine ? 'Ty' : other.name}, {time(d)}:</span>
                                <p><MessageText text={m.text} /></p>
                                { m.failed
                                    ? <span className={styles.st}><SvgIcon name="alert" size="sm" />Nie wysłano<button onClick={() => send(m)} type="button">Ponów</button></span>
                                    : <time aria-hidden="true" dateTime={m.at}>{time(d)}</time> }
                            </li>,
                        ]
                    }) }
                </ol>
            </div>
            { other.deleted
                ? <div className={styles.composer}><p className={styles.closed}><SvgIcon name="info" size="sm" />Użytkownik usunął konto — nie można odpowiedzieć.</p></div>
                : data.blocking
                    ? <div className={styles.composer}><p className={styles.closed}><span>Zablokowałeś tego użytkownika — nie może do Ciebie pisać.</span><button className={mix([styles.btn, styles.secondary])} onClick={() => block(false)} type="button">Odblokuj</button></p></div>
                    : <div className={styles.composer}>
                        { problem && <div className={styles.alert} role="alert"><strong>{problem.title}</strong><p>{problem.text}</p></div> }
                        <form className={styles.form} noValidate onSubmit={(e) => { e.preventDefault(); send() }}>
                            <label className={styles.sr} htmlFor="msg-text">Wiadomość do: {other.name}</label>
                            <textarea aria-describedby="msg-hint" id="msg-text" onChange={(e) => setText(e.target.value)} onKeyDown={onKeyDown} placeholder="Napisz wiadomość…" ref={taRef} rows={1} value={text} />
                            <button aria-label="Wyślij" className={styles.send} disabled={sending} type="submit"><SvgIcon name="send" /></button>
                        </form>
                        <div className={styles.hint} id="msg-hint">
                            <span className={styles.kbd}>Enter — wyślij · Shift+Enter — nowa linia</span>
                            <span className={count > MAX ? styles.over : undefined}>{count > MAX - 200 ? `${count} / ${MAX}` : ''}</span>
                        </div>
                    </div> }
            { menu && <MoreMenu items={items} onClose={() => setMenu(null)} onPick={action} opener={menu} title={data.title} /> }
            { dialog === 'report' && <ReportDialog onClose={() => setDialog(null)} onSubmit={report} opener={menu || headRef.current} /> }
            { dialog === 'block' && <BlockDialog name={other.name} onClose={() => setDialog(null)} onConfirm={() => block(true)} opener={headRef.current} /> }
        </>
    )
}
